import {
  Component, OnInit, OnDestroy, inject, signal, effect, computed,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { filter, pairwise, Subscription } from 'rxjs';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { ControlService } from '../../library/control.service';
import { RealtimeService } from '../../library/realtime/realtime.service';
import { UserVideoService } from '../../library/user-video.service';
import { DeviceSettingsService } from '../../library/device-settings.service';
import { PlayerService } from '../../library/player.service';
import { CallStoreService } from '../../library/call-store/call-store.service';
import { CallService } from 'src/app/services/learner/call.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { PathwayOrchestratorService } from '../../call-pathway/pathway-orchestrator.service';
import { PathwayConstants } from 'src/app/utils/constants/pathway/pathway.constants';
import { PathwayStepType } from 'src/app/utils/enums/learner/pathway.enum';
import { PathwayLocalStorageUtil } from 'src/app/utils/pathway-localstorage.util';
import { PathwaySubtitleService } from '../../call-pathway/pathway-subtitle.service';
import { AppConstants } from 'src/app/app-constants';
import { ICallRealtime } from 'src/app/interfaces/icall-realtime';
import { TranslationService } from 'src/app/services/translation.service';

type DeviceSettingsModalContext = Readonly<{
  wasPausedBeforeOpen: boolean;
  micOnBeforeOpen: boolean;
  videoOnBeforeOpen: boolean;
}>;

@Component({
  host: { class: 'theme-dark' },
  selector: 'app-assessment-call-page',
  standalone: false,
  templateUrl: './assessment-call-page.component.html',
  styleUrls: ['./assessment-call-page.component.scss'],
  providers: [
    ControlService,
    RealtimeService,
    UserVideoService,
    DeviceSettingsService,
    PlayerService,
    CallStoreService,
    PathwayOrchestratorService,
    PathwaySubtitleService,
    { provide: TRANSLOCO_SCOPE, useValue: 'learningPathway' },
    { provide: TRANSLOCO_SCOPE, useValue: 'call' },
  ],
})
export class AssessmentCallPageComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly orchestrator = inject(PathwayOrchestratorService);
  private readonly callService = inject(CallService);
  readonly realtimeService = inject(RealtimeService);
  readonly controlService = inject(ControlService);
  readonly userVideoService = inject(UserVideoService);
  readonly deviceSettingsService = inject(DeviceSettingsService);
  readonly playerService = inject(PlayerService);
  readonly callStore = inject(CallStoreService);
  private readonly loggerService = inject(LoggerService);
  private readonly pathwaySubtitleService = inject(PathwaySubtitleService);
  private readonly translationService = inject(TranslationService);

  readonly mainIsLoading = signal(true);
  readonly captionText = signal('');
  readonly showAvatarPhoto = signal(false);
  readonly avatarPhotoUrl = signal('');
  readonly disableConfirmActionButton = signal(false);
  readonly callStarted = signal(false);
  readonly title = signal('');

  private deviceSettingsModalContext: DeviceSettingsModalContext | null = null;

  protected readonly subtitle = computed(() =>
    this.pathwaySubtitleService.getSubtitle({
      callFlow: 'call-main',
      stepType: PathwayStepType.ASSESSMENT,
      conversationState: null,
      isAvatarSpeaking: this.callStore.getIsAvatarSpeaking(),
      isVideoPlaying: false,
      isMicOn: this.callStore.getIsMicOn(),
      callStarted: this.callStarted(),
      isAvatarThinking: this.callStore.getIsAvatarThinking(),
      quizResultTtsPlaying: false,
      isPreamble: false,
      captionText: this.captionText(),
      hasWelcomeMessagePlayed: true,
    })
  );

  protected enrollmentUuid = '';
  private enrollmentStepUuid = '';
  private moduleAssignmentUuid = '';
  private practiceType = PathwayConstants.PRACTICE_TYPE_PATHWAY;
  private currentCallUuid = '';
  private subscriptions: Subscription[] = [];

  constructor() {
    // Wire services together — mirrors what CallComponent does in its constructor
    this.playerService.setControlService(this.controlService);

    this.controlService.setPlayerService(this.playerService);
    this.controlService.setUserVideoService(this.userVideoService);
    this.controlService.setDeviceSettingsService(this.deviceSettingsService);
    this.controlService.setRealtimeService(this.realtimeService);

    this.userVideoService.setControlService(this.controlService);
    this.userVideoService.setDeviceSettingsService(this.deviceSettingsService);
    this.userVideoService.setPlayerService(this.playerService);

    this.deviceSettingsService.setPlayerService(this.playerService);
    this.deviceSettingsService.setControlService(this.controlService);
    this.deviceSettingsService.setRealtimeService(this.realtimeService);

    this.realtimeService.setDeviceSettingsService(this.deviceSettingsService);
    this.realtimeService.setPlayerService(this.playerService);
    this.realtimeService.setControlService(this.controlService);

    effect(() => {
      const status = this.orchestrator.flowStatus();
      if (status === 'running') {
        const response = this.orchestrator.runStepResponse();
        if (!response?.callUuid) {
          this.loggerService.error('[ASSESSMENT PAGE] Missing callUuid in runStep response');
          this.mainIsLoading.set(false);
          return;
        }
        this.currentCallUuid = response.callUuid;
        this.title.set(response.stepName || response.callName || '');
        this.initiateSimulation();
      } else if (status === 'step_completed' || status === 'pathway_completed') {
        this.navigateToPathway();
      } else if (status === 'error' || status === 'blocked') {
        this.navigateToPathway();
      }
    });

    effect(() => {
      const speech = this.playerService.avatarSpeech?.() || '';
      this.captionText.set(speech);
    });
  }

  ngOnInit(): void {
    this.translationService.loadScopedTranslation('learningPathway');
    this.translationService.loadScopedTranslation('call');

    this.deviceSettingsService.validateDeviceAccess();

    const params = this.route.snapshot.queryParams;
    this.enrollmentUuid = params['enrollmentUuid'] ?? '';
    this.enrollmentStepUuid = params['enrollmentStepUuid'] ?? '';
    this.moduleAssignmentUuid = params['moduleAssignmentUuid'] ?? '';
    this.practiceType = params['practiceType'] ?? PathwayConstants.PRACTICE_TYPE_PATHWAY;

    if (!this.enrollmentUuid || !this.enrollmentStepUuid || !this.moduleAssignmentUuid) {
      this.router.navigate(['/assignments']);
      return;
    }

    this.subscriptions.push(
      this.realtimeService.onError$.subscribe((error) => {
        this.loggerService.error('[ASSESSMENT PAGE] Realtime error', error);
      })
    );

    this.subscriptions.push(
      this.deviceSettingsService.displayModal$
        .pipe(
          pairwise(),
          filter(([prev, cur]) => prev === true && cur === false)
        )
        .subscribe(() => this.handleDeviceSettingsModalClosed())
    );

    const userUuid = localStorage.getItem('user_uuid') || '';
    this.callStore.setUserUuid(userUuid);
    this.callStore.setModuleAssignmentType(PathwayConstants.PRACTICE_TYPE_PATHWAY);
    this.controlService.isRealTimeCall = true;

    this.orchestrator.runStep(this.enrollmentUuid, this.enrollmentStepUuid);
  }

  private initiateSimulation(): void {
    this.subscriptions.push(
      this.callService
        .initiatePathwayStepSimulation(
          this.moduleAssignmentUuid,
          this.currentCallUuid,
          this.practiceType,
          PathwayStepType.ASSESSMENT,
          this.enrollmentStepUuid,
        )
        .subscribe({
          next: async (res) => {
            try {
              const conversationId = res.conversationId ?? '';
              const conversationUrl = res.conversationUrl ?? '';
              if (!conversationId || !conversationUrl) {
                this.loggerService.error('[ASSESSMENT PAGE] Missing conversation details');
                this.mainIsLoading.set(false);
                return;
              }

              this.callStore.setPracticeCallUuid(res.practiceCallUuid);
              this.callStore.setCallUuid(this.currentCallUuid);
              this.callStore.setCallType(PathwayStepType.ASSESSMENT);
              this.callStore.setCallWords(res.callWords ?? []);
              this.callStore.setReplaceWords(res.replaceWords ?? []);
              this.callStore.setDisplayPostCall(false);
              this.callStore.setCurrentView(AppConstants.VIEW_PRACTICE);

              if (res.callName) {
                this.title.set(res.callName);
              }
              this.captionText.set(res.originalWelcomeMessage ?? '');
              this.avatarPhotoUrl.set(res.avatarThumbnail ?? '');

              const realtimePayload: ICallRealtime = {
                practiceCallUuid: res.practiceCallUuid,
                conversationId,
                conversationUrl,
                callName: res.callName ?? '',
                avatarName: res.avatarName ?? this.playerService.avatarName ?? '',
                pronounceWords: res.pronounceWords ?? [],
                resumeMessage: res.resumeMessage ?? [],
                visualAid: res.visualAid ?? undefined,
                customBackgroundUrl: res.customBackgroundUrl ?? undefined,
                hideResultsForLearners: res.hideResultsForLearners ?? null,
              };

              await this.realtimeService.initializeFromStartConversationResponse(realtimePayload, false);
              this.realtimeService.conversationInitializedByPathwayFlow = true;
              this.mainIsLoading.set(false);
              this.callStarted.set(true);

              // Assessment steps auto-complete once the simulation is initiated
              this.orchestrator.completeStepSilently(this.enrollmentUuid, this.enrollmentStepUuid);
            } catch (error) {
              this.loggerService.error('[ASSESSMENT PAGE] Error initializing simulation', error);
              this.mainIsLoading.set(false);
            }
          },
          error: (err) => {
            this.loggerService.error('[ASSESSMENT PAGE] initiatePathwayStepSimulation error', err);
            this.mainIsLoading.set(false);
          },
        })
    );
  }

  async handleCompleteStep(): Promise<void> {
    this.mainIsLoading.set(true);
    await this.endRealtimeCall();
    this.navigateToPathway();
  }

  async handleLeaveStep(): Promise<void> {
    this.mainIsLoading.set(true);
    await this.endRealtimeCall();
    this.navigateToPathway();
  }

  private async endRealtimeCall(): Promise<void> {
    try {
      this.realtimeService.endRealtimeConversation(true, true);
    } catch (error) {
      this.loggerService.error('[ASSESSMENT PAGE] Error ending realtime conversation:', error);
    }
  }

  protected handleSettingsClick(): void {
    this.openDeviceSettingsModal();
  }

  private openDeviceSettingsModal(): void {
    this.deviceSettingsModalContext = {
      wasPausedBeforeOpen: this.callStore.getIsPaused(),
      micOnBeforeOpen: this.callStore.getIsMicOn(),
      videoOnBeforeOpen: this.controlService.isVideoOn,
    };
    this.deviceSettingsService.displayDeviceSettingsModal();
  }

  private handleDeviceSettingsModalClosed(): void {
    const ctx = this.deviceSettingsModalContext;
    if (!ctx) return;
    this.deviceSettingsModalContext = null;

    if (this.callStore.getIsAudioCallMode()) return;
    if (ctx.wasPausedBeforeOpen) return;

    const isPaused = this.callStore.getIsPaused();
    if (!isPaused) return;

    this.controlService.isMicOnBeforePause = ctx.micOnBeforeOpen;
    this.controlService.isVideoOnBeforePause = ctx.videoOnBeforeOpen;

    if (this.controlService.isRealTimeCall) {
      if (!this.realtimeService.isGuided()) {
        this.realtimeService.unpauseCall();
      }
      return;
    }
    this.controlService.unpauseCall();
  }

  protected onCompleteStepResult(): void {
    this.navigateToPathway();
  }

  private navigateToPathway(): void {
    PathwayLocalStorageUtil.setAssessmentExitAnimate();
    PathwayLocalStorageUtil.setAssessmentPracticeCallUuid(this.callStore.getPracticeCallUuid());
    this.router.navigate(['/learning-pathway'], {
      queryParams: {
        moduleAssignmentUuid: this.moduleAssignmentUuid,
        practiceType: this.practiceType,
      },
    });
  }

  ngOnDestroy(): void {
    this.realtimeService.stopChromaKeying();
    this.callStore.setUserMicPreference(true);
    this.callStore.setIsAvatarThinking(false);
    this.pathwaySubtitleService.reset();
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
    this.orchestrator.resetState();
  }
}
